import { faSave, faDownload } from '@fortawesome/free-solid-svg-icons'
import { CounterContext } from 'context/CounterContext'
import { useOrsServiceExceptionFindAllByStatusQuery } from 'modules/contractedService/application/ors/exception/find/useOrsServiceExceptionFindQuery'
import { OrsServiceExceptionStateSet } from 'modules/contractedService/application/ors/exception/update/dto/OrsServiceExceptionUpdateStateRequest'
import { useOrsServiceExceptionUpdateStateQuery } from 'modules/contractedService/application/ors/exception/update/useOrsServiceExceptionUpdateStateQuery'
import { ExceptionStatus } from 'modules/contractedService/domain/ors/exception/ExceptionStatus'
import { OrsServiceException } from 'modules/contractedService/domain/ors/exception/OrsServiceException'
import { OrsServiceExceptionRepository } from 'modules/contractedService/domain/ors/exception/repository/OrsServiceExceptionRepository'
import { OrsServiceExceptionState } from 'modules/contractedService/domain/ors/exception/state/OrsServiceExceptionState'
import { DataTablePageEvent } from 'primereact/datatable'
import { RefObject, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { ContractedServiceTypeId } from 'modules/contractedService/domain/ContractedServiceType'
import * as XLSX from 'xlsx'
import { ColData, UfinetButton, TooltipButton, TableHandle } from 'ufinet-web-components'
import {
	AuthContext,
	Authority,
	defaultPageNumber,
	defaultPageSize,
	noContentPlaceholder,
	useTranslator,
} from 'ufinet-web-functions'
import { useExceptionManagementColsData } from './useExceptionManagementColsData'

type HookInput = {
	orsServiceExceptionRepository: OrsServiceExceptionRepository
	variant: ExceptionStatus
	tableRef: RefObject<TableHandle>
	onClearFilters?: () => void
}

type HookOutput = {
	orsServiceExceptions: {
		all: OrsServiceException[]
	}
	heading: {
		visible: boolean
		content: string
	}
	table: {
		loading: boolean
		columns: ColData[]
		headerButtons?: JSX.Element
		onPFSEvent: (event: DataTablePageEvent) => void
		onValuesDisplayedChange: (valuesDisplayed: OrsServiceException[]) => void
	}
}

const tooltipButtonStyle = (): React.CSSProperties => ({
	background: '#0065a4',
	border: 'none',
	padding: '0.50rem',
	margin: '0 0.3rem',
	boxShadow: 'none',
	color: 'white',
	transition: 'all 0.2s ease-in-out',
})

function useExceptionManagementTable({
	orsServiceExceptionRepository,
	variant,
	onClearFilters,
	tableRef,
}: HookInput): HookOutput {
	const authData = useContext(AuthContext)
	const { refetchTotalExceptions } = useContext(CounterContext)
	const authRoles = authData.userData?.authorities || []
	const permissions = Authority.getGseExceptionPermissions(authRoles)

	const translate = useTranslator()
	const [tablePFSEvent, setTablePFSEvent] = useState<DataTablePageEvent>()
	const [isDownloadDisabled, setIsDownloadDisabled] = useState(false)
	const orsExceptionStartIndex = useMemo(
		() => (tablePFSEvent?.page ?? defaultPageNumber) * (tablePFSEvent?.rows ?? defaultPageSize),
		[tablePFSEvent?.page, tablePFSEvent?.rows]
	)
	const orsExceptionEndIndex = useMemo(
		() => orsExceptionStartIndex + (tablePFSEvent?.rows ?? defaultPageSize),
		[orsExceptionStartIndex, tablePFSEvent?.rows]
	)

	const [orsServiceExceptions, setOrsServiceExceptions] = useState<OrsServiceException[]>([])
	const [displayedOrsServiceExceptions, setDisplayedOrsServiceExceptions] = useState<OrsServiceException[]>([])
	const visibleOrsServiceExceptions = useMemo(
		() => displayedOrsServiceExceptions.slice(orsExceptionStartIndex, orsExceptionEndIndex),
		[displayedOrsServiceExceptions, orsExceptionEndIndex, orsExceptionStartIndex]
	)
	const [nonManagedOrsServiceExceptions, setNonManagedOrsServiceExceptions] = useState<OrsServiceException[]>([])
	const [orsAllServiceExceptionsHeaderCheck, setOrsAllServiceExceptionsHeaderCheck] =
		useState<OrsServiceExceptionState>(OrsServiceExceptionState.EMPTY)

	const [orsServiceInitialStates, setOrsServiceInitialStates] = useState<
		{ id: string; state: OrsServiceExceptionState }[]
	>([])

	const orsServiceCurrentStates = useMemo(
		() =>
			orsServiceExceptions.map((it) => ({
				id: it.service.id,
				state: it.exception.state,
			})),
		[orsServiceExceptions]
	)

	const orsServiceModifiedStates = useMemo(
		() =>
			orsServiceCurrentStates.filter((currentState) =>
				orsServiceInitialStates.find(
					(initialState) => initialState.id === currentState.id && initialState.state !== currentState.state
				)
			),
		[orsServiceCurrentStates, orsServiceInitialStates]
	)

	const {
		isLoading: loadingServiceExceptions,
		isFetching: fetchingServiceExceptions,
		refetch: fetchExceptions,
	} = useOrsServiceExceptionFindAllByStatusQuery(orsServiceExceptionRepository, variant, {
		onSuccess: (serviceExceptions) => {
			setOrsServiceExceptions(serviceExceptions)
			setDisplayedOrsServiceExceptions(serviceExceptions)
			setOrsServiceInitialStates(
				serviceExceptions.map((it) => ({
					id: it.service.id,
					state: it.exception.state,
				}))
			)
			setNonManagedOrsServiceExceptions(
				serviceExceptions.filter((it) => it.exception.state === OrsServiceExceptionState.EMPTY)
			)
		},
		onError: () => toast.error(translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.FIND.ERROR')),
	})
	const { isLoading: updatingServiceStates, mutate: updateServiceStates } = useOrsServiceExceptionUpdateStateQuery(
		orsServiceExceptionRepository,
		{
			onSuccess: () => {
				toast.success(translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.STATE.UPDATE.SUCCESS'))
				setOrsAllServiceExceptionsHeaderCheck(OrsServiceExceptionState.EMPTY)
				refetchTotalExceptions()
				fetchExceptions()
				onClearFilters?.()
			},
			onError: () => toast.error(translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.STATE.UPDATE.ERROR')),
		}
	)

	const onExceptionStateChange = useCallback(
		({
			serviceIds,
			selectedState = OrsServiceExceptionState.EMPTY,
		}: {
			serviceIds: string[]
			selectedState: OrsServiceExceptionState
		}) => {
			if (
				orsAllServiceExceptionsHeaderCheck !== OrsServiceExceptionState.EMPTY &&
				orsAllServiceExceptionsHeaderCheck !== selectedState
			) {
				setOrsAllServiceExceptionsHeaderCheck(OrsServiceExceptionState.EMPTY)
			}

			setOrsServiceExceptions((oldValues) =>
				oldValues.map((serviceException) => ({
					...serviceException,
					exception: {
						...serviceException.exception,
						state: serviceIds.includes(serviceException.service.id) ? selectedState : serviceException.exception.state,
					},
				}))
			)
			setDisplayedOrsServiceExceptions((oldValues) =>
				oldValues.map((serviceException) => ({
					...serviceException,
					exception: {
						...serviceException.exception,
						state: serviceIds.includes(serviceException.service.id) ? selectedState : serviceException.exception.state,
					},
				}))
			)
		},
		[orsAllServiceExceptionsHeaderCheck]
	)

	const onExceptionAllStateChange = useCallback(
		(selectedState: OrsServiceExceptionState) => {
			const serviceIds = visibleOrsServiceExceptions.map((serviceException) => serviceException.service.id)

			onExceptionStateChange({ serviceIds, selectedState })
			setOrsAllServiceExceptionsHeaderCheck(selectedState)
		},
		[onExceptionStateChange, visibleOrsServiceExceptions]
	)

	useEffect(() => {
		const visibleExceptionStates = visibleOrsServiceExceptions.map(
			(serviceException) => serviceException.exception.state
		)

		setOrsAllServiceExceptionsHeaderCheck(
			visibleExceptionStates.length === 0
				? OrsServiceExceptionState.EMPTY
				: visibleExceptionStates.every((it) => it === OrsServiceExceptionState.WITH_EXCEPTION)
				? OrsServiceExceptionState.WITH_EXCEPTION
				: visibleExceptionStates.every((it) => it === OrsServiceExceptionState.WITHOUT_EXCEPTION)
				? OrsServiceExceptionState.WITHOUT_EXCEPTION
				: OrsServiceExceptionState.EMPTY
		)
	}, [displayedOrsServiceExceptions, orsExceptionEndIndex, orsExceptionStartIndex, visibleOrsServiceExceptions])

	const headerButtons = useMemo(
		() => (
			<>
				{(permissions.canWrite || permissions.canUpdate) && (
					<UfinetButton
						className="me-3"
						icon={faSave}
						onClick={() => {
							const updateSet: OrsServiceExceptionStateSet = {}
							orsServiceModifiedStates.forEach(({ id, state }) => (updateSet[id] = state))
							updateServiceStates({ updates: updateSet })
						}}
						content={translate('SAVE')}
						isDisabled={orsServiceModifiedStates.length === 0 || updatingServiceStates}
					/>
				)}
				{(permissions.canWrite || permissions.canUpdate || displayedOrsServiceExceptions.length === 0) && (
					<TooltipButton
						buttonProps={{
							className: 'me-3',
							icon: faDownload,
							style: tooltipButtonStyle(),
							onClick: () => {
								setIsDownloadDisabled(true)
								exportToExcelWithTable(displayedOrsServiceExceptions, translate)
								setTimeout(() => setIsDownloadDisabled(false), 3000)
							},
							isDisabled:
								isDownloadDisabled ||
								!(orsServiceModifiedStates.length === 0) ||
								loadingServiceExceptions ||
								fetchingServiceExceptions ||
								updatingServiceStates ||
								displayedOrsServiceExceptions.length === 0,
							content: translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.DOWNLOAD'),
						}}
					>
						{!(orsServiceModifiedStates.length === 0)
							? translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.TOOLTIP')
							: ''}
					</TooltipButton>
				)}
			</>
		),
		[
			orsServiceModifiedStates,
			permissions,
			translate,
			updateServiceStates,
			updatingServiceStates,
			isDownloadDisabled,
			loadingServiceExceptions,
			fetchingServiceExceptions,
			displayedOrsServiceExceptions,
		]
	)
	const { columns: tableColumns } = useExceptionManagementColsData({
		canEditState: permissions.canWrite || permissions.canUpdate,
		orsServiceExceptions,
		orsAllServiceExceptionsHeaderCheck,
		onExceptionStateChange,
		onExceptionAllStateChange,
	})

	return {
		orsServiceExceptions: {
			all: orsServiceExceptions,
		},
		heading: {
			visible: permissions.canWrite || permissions.canUpdate,
			content: `${translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.PENDING')}: ${
				loadingServiceExceptions || fetchingServiceExceptions
					? noContentPlaceholder
					: nonManagedOrsServiceExceptions.length
			}`,
		},
		table: {
			loading: loadingServiceExceptions || fetchingServiceExceptions || updatingServiceStates,
			columns: tableColumns,
			headerButtons,
			onPFSEvent: setTablePFSEvent,
			onValuesDisplayedChange: setDisplayedOrsServiceExceptions,
		},
	}
}
function exportToExcelWithTable(data: OrsServiceException[], translate: (key: string) => string) {
	const shouldHideExceptionColumn = data.every((row) => row.exception.state === OrsServiceExceptionState.EMPTY)

	const headers = [
		!shouldHideExceptionColumn ? translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.STATE.REPORT') : null,
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.ADMINISTRATIVE_CODE'),
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.NAME'),
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.CLIENT_NAME'),
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.TYPE'),
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.TERM'),
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.ARC_DIFFERENTIAL'),
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.KAM_NAME'),
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.STATE'),
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.DAYS_CURRENT_STATUS'),
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.ARC_COUNTRY'),
	].filter(Boolean)

	const rows = data.map((row) => {
		const estadoExcepcion =
			row.exception.state === OrsServiceExceptionState.WITH_EXCEPTION
				? translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.STATE.WITH_EXCEPTION')
				: row.exception.state === OrsServiceExceptionState.WITHOUT_EXCEPTION
				? translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.STATE.WITHOUT_EXCEPTION')
				: ''

		const rowData = [
			!shouldHideExceptionColumn ? estadoExcepcion : null,
			row.service.administrativeCode ?? '',
			row.service.name ?? '',
			row.client?.name ?? '',
			translate(`CONTRACT.SERVICE.TYPE.${ContractedServiceTypeId[row.service.type]}`) ?? '',
			row.term ?? '',
			row.arcDifferential ?? '',
			row.kam?.name ?? '',
			translate(`CONTRACT.SERVICE.STATE.${row.service.state.value}`) ?? '',
			row.daysCurrentStatus ?? '',
			row.arcCountry?.name ?? '',
		]

		return rowData.filter((cell) => cell !== null)
	})

	const worksheetData = [headers, ...rows]
	const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
	const now = new Date()
	const formattedDate = now.toISOString().replace(/[:]/g, '-').replace(/\..+/, '')
	const workbook = XLSX.utils.book_new()
	XLSX.utils.book_append_sheet(workbook, worksheet, 'ORS Exceptions')
	XLSX.writeFile(
		workbook,
		translate('CONTRACT.SERVICE.ORS.SERVICE.EXCEPTION.EXCEL_TITLE') + '_' + formattedDate + '.xlsx'
	)
}

export { useExceptionManagementTable }
